import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Event/AnalyticsEventSettings/AnalyticsEventSettings.less";
import { Card, type SelectOption, Select, SettingsLayout, Switch } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectAnalyticsEvent, setAnalyticsEventAction } from "Data/Redux/Slices/Settings/Event/Analytics";
import { produce } from "immer";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { isEqual } from "lodash";
import { eventPopoverContent } from "Data/Objects/Event";

const intervalSelectOptions: SelectOption[] = [ 0, 5, 10, 15, 20, 25, 30 ].map(value => ({
	label: value === 0 ? "Off" : `${ value }`,
	value
}));

type AnalyticsEventForm = {
	eventName: HTMLInputElement;
	alarmOut: HTMLInputElement;
	snapshot: HTMLInputElement;
	whiteLight: HTMLInputElement;
	recording: HTMLInputElement;
	email: HTMLInputElement;
	ftp: HTMLInputElement;
} & HTMLFormElement;

function AnalyticsEventSettings() {
	const dispatch = useAppDispatch();
	const analyticsEventSettings = useAppSelector(selectAnalyticsEvent);
	const [ localAnalyticsEventSettings, setLocalAnalyticsEventSettings ] = useState(analyticsEventSettings);
	const setAnalyticsEvent = () => dispatch(setAnalyticsEventAction(localAnalyticsEventSettings));
	const disabled = isEqual(localAnalyticsEventSettings, analyticsEventSettings);
	const {
		interval, alarmOut, snapshot, whiteLight, redBlueLight, audioAlarm, recording, email, ftp
	} = localAnalyticsEventSettings;

	const handleAlarmOutChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.alarmOut = newEnabled;
		}));
	};

	const handleSnapshotChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.snapshot = newEnabled;
		}));
	};

	const handleWhiteLightChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.whiteLight = newEnabled;
		}));
	};

	const handleRedBlueLightChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.redBlueLight = newEnabled;
		}));
	};

	const handleAudioAlarmChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.audioAlarm = newEnabled;
		}));
	};

	const handleRecordingChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.recording = newEnabled;
		}));
	};

	const handleEmailChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.email = newEnabled;
		}));
	};

	const handleFtpChange = (newEnabled: boolean) => {
		setLocalAnalyticsEventSettings(produce(localAnalyticsEventSettings, draft => {
			draft.ftp = newEnabled;
		}));
	};

	const handleFormSubmit = (event: ReactSubmitEvent<AnalyticsEventForm>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setAnalyticsEvent();
		}
	};

	const handleFormReset = (event: FormEvent<AnalyticsEventForm>) => {
		event.preventDefault();

		setLocalAnalyticsEventSettings(analyticsEventSettings);
	};

	const handleIntervalSelect = (newInterval: string | null) => {
		if (!newInterval) {
			return;
		}

		const newLocalState = produce(localAnalyticsEventSettings, draft => {
			draft.interval = +newInterval;
		});

		setLocalAnalyticsEventSettings(newLocalState);
	};

	return (
		<SettingsLayout.Form
			className="analytics-event-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="AI Analytics Event" popoverContent={ eventPopoverContent }>
					<div className="analytics-content">
						<div className="form-row">
							<span>Ignoring Interval</span>
							<Select
								allowClear={ false }
								className="form-select"
								onSelect={ handleIntervalSelect }
								options={ intervalSelectOptions }
								value={ interval }
							/>
						</div>
						<div className="form-row">
							<span>Alarm Out</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAlarmOutChange } checked={ alarmOut } />
							</div>
						</div>
						<div className="form-row">
							<span>Snapshot</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleSnapshotChange } checked={ snapshot } />
							</div>
						</div>
						<div className="form-row">
							<span>White Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleWhiteLightChange }checked={ whiteLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Red & Blue Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRedBlueLightChange } checked={ redBlueLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Audio Alarm</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAudioAlarmChange } checked={ audioAlarm } />
							</div>
						</div>
						<div className="form-row">
							<span>Recording</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRecordingChange } checked={ recording } />
							</div>
						</div>
						<div className="form-row">
							<span>E-Mail</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleEmailChange } checked={ email } />
							</div>
						</div>
						<div className="form-row">
							<span>FTP</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleFtpChange } checked={ ftp } />
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(AnalyticsEventSettings);
